import { ENGLISH } from 'app/constants/enums/Language';
import * as Visibility from 'app/constants/enums/Visibility';
import { MASTER, TEMPLATE } from 'app/constants/LessonTypes';
import * as Schema from 'app/constants/schemas';
import { authenticate, sessionRequest } from 'app/models/auth/actions';
import { removeListElement } from 'app/models/entities/actions';
import { LESSON_VALIDATION_SOURCE } from 'app/models/entities/constants';
import { getEntityOfType, getEntityOfTypeOrThrow } from 'app/models/entities/selectors';
import { isValidationError } from 'app/models/publishedLessonAdmin/remote';
import ApplicationError from 'app/models/request/ApplicationError';
import { clearValidationErrors, setValidationErrors } from 'app/models/validations/actions';
import rootReducer from 'app/reducers';
import {
  discardLocalChanges,
  serializeEdits,
} from 'app/remote';
import { getResponseId } from 'app/remote/response';
import { notify } from 'app/services/errorReporter';
import vmClient from 'app/services/vmClient';
import * as API from 'app/utils/api';
import { List } from 'immutable';
import { Draft, SearchParameter } from 'app/constants/QueryParameter';
import { ALL_RIGHTS_RESERVED } from 'app/constants/enums/CopyrightLicense';
import { ARTICLE } from 'app/constants/enums/BibliographyType';
import {
  saveEntityDeep,
  saveEntityOnce,
} from './base';
import {
  fetchEntity,
  fetchEntities,
  entityActions,
  createEntity,
  postAndUpdate,
  externalAssociationActions,
} from './next';

export {
  saveEntity,
  saveEntityDeep,
  saveEntityOnce,
  RetryLimitExceeded,
} from './base';

export {
  fetchEntities,
  entityActions,
  createEntity,
  postAndUpdate,
} from './next';


export const {
  fetch: fetchBanner,
  fetchList: fetchBanners,
  search: searchBanners,
  remove: removeBanner,
  save: saveBanner,
} = entityActions(API.bannerRoutes, Schema.Banner);

export const { fetchList: fetchServiceDomains }  =
  entityActions(API.serviceDomainRoutes, Schema.ServiceDomain);

export const {
  fetch: fetchOrganization,
  fetchList: fetchOrganizations,
} = entityActions(API.organizationRoutes, Schema.Organization);

export const {
  fetch: fetchOrganizationDetail,
  fetchList: fetchOrganizationDetails,
  remove: removeOrganizationDetails,
  save: saveOrganizationDetails,
} = entityActions(API.organizationDetailsRoutes, Schema.OrganizationDetails);


export const {
  fetch: fetchBrand,
  fetchList: fetchBrands,
  remove: removeBrand,
  save: saveBrand,
} = entityActions(API.brandRoutes, Schema.Brand);

export const {
  fetchList: fetchCurricula,
  // we don't use the other endpoints just yet
} = entityActions(API.curriculumRoutes, Schema.Curriculum);

export const {
  fetch: fetchCurriculumCategories,
  fetchCanonical: fetchCurriculumCategoriesCanonical,
  search: searchCurriculumCategories,
  save:  saveCurriculumCategories,
} = externalAssociationActions(API.curriculumCategoryRoutes, Schema.CurriculumCategories);

export const {
  fetch: fetchCategory,
  fetchList: fetchCategories,
  search: searchCategories,
  remove: removeCategory,
  save: saveCategory,
} = entityActions(API.categoryRoutes, Schema.Category);

export const {
  fetch: fetchCategoryCourses,
  fetchCanonical: fetchCategoryCoursesCanonical,
  search: searchCategoryCourses,
  save:  saveCategoryCourses,
} = externalAssociationActions(API.categoryCourseRoutes, Schema.CategoryCourses);

export const {
  fetch: fetchCourse,
  fetchList: fetchCourses,
  search: searchCourses,
  remove: removeCourse,
  save: saveCourse,
} = entityActions(API.courseRoutes, Schema.Course);

const emptyCourse = new Schema.Course({
  nameText: new Schema.Text({ text: '' }),
  descriptionText: new Schema.Text({ text: '' }),
  visibility: Visibility.PRIVATE.id,
  targetLevelRange: new Schema.IntegerRange({ min: 1, max: 10 }),
});

export const createCourse = createEntity(
  data => sessionRequest(API.postCourses(data)),
  emptyCourse,
);

export const createCourseInCategory = (categoryId, courseData) => createEntity(
  data => sessionRequest(API.putCategoryCourses(categoryId, data)),
  emptyCourse,
)(courseData);

export const {
  fetch: fetchCourseLessonHeaders,
  fetchCanonical: fetchCourseLessonHeadersCanonical,
  search: searchCourseLessonHeaders,
  save:  saveCourseLessonHeaders,
} = externalAssociationActions(
  API.courseLessonHeaderRoutes,
  Schema.CourseLessonHeaders,
  {
    canonicalArgs: {
      type: MASTER.id,
    },
  },
);

export const fetchCurrentPublishedCourseLessons = (id, data, cancelToken) => fetchEntities({
  params: API.getCurrentPublishedCourseLessonHeaders(id, data),
  type: Schema.LessonHeader,
  cancelToken,
});

export const {
  fetch: fetchLesson,
  fetchList: fetchLessons,
  search: searchLessons,
  remove: removeLesson,
  save: saveLesson,
} = entityActions(API.lessonRoutes, Schema.Lesson);

export const {
  fetch: fetchLessonHeader,
  fetchList: fetchLessonHeaders,
  search: searchLessonHeaders,
} = entityActions(API.lessonHeaderRoutes, Schema.LessonHeader);

export const fetchCurrentLessonHeader = (lessonId, cancelToken) => fetchEntity({
  params: API.getCurrentLessonHeader(lessonId),
  type: Schema.LessonHeader,
  cancelToken,
});

export const fetchCurrentPublishedLessonHeaders = (...args) => fetchEntities({
  params: API.getCurrentPublishedLessonHeaders(...args),
  type: Schema.LessonHeader,
});

const makeEmptyLesson = ({
  organizationId,
  lessonType = MASTER.id,
}) => new Schema.Lesson({
  titleText: new Schema.Text({ text: '' }),
  language:   ENGLISH.id,
  lessonType,
  owner: new Schema.OrganizationReference({
    id: organizationId,
  }),
});

export const createLesson = ({
  organizationId,
}) => createEntity(
  data => sessionRequest(API.postLessons(data)),
  makeEmptyLesson({ organizationId }),
);

export const createLessonInCourse = ({
  organizationId,
  courseId,
  lessonData,
}) => createEntity(
  data => sessionRequest(API.putCourseLessons(courseId, data)),
  makeEmptyLesson({ organizationId }),
)(lessonData);

export const createLessonTemplate = ({
  organizationId,
}) => createEntity((data) => sessionRequest(API.postLessons(data)),
  makeEmptyLesson({
    organizationId,
    lessonType: TEMPLATE.id,
    owner: new Schema.OrganizationReference({
      id: organizationId,
    }),
  }));

export const createLessonFromTemplate = (id) => dispatch =>
  postAndUpdate(sessionRequest(API.duplicateLesson(id)))(dispatch);

export const {
  fetch: fetchWord,
  fetchList: fetchWords,
  search: searchWords,
  remove: removeWord,
  save: saveWord,
} = entityActions(API.wordRoutes, Schema.Word);

export const fetchWordLessonHeaders = (...args) => fetchEntities({
  params: API.getWordLessonHeaders(...args),
  type: Schema.LessonHeader,
});

export const {
  fetch: fetchWordSentence,
  fetchList: fetchWordSentences,
  remove: removeWordSentence,
  save: saveWordSentence,
} = entityActions(API.wordSentenceRoutes, Schema.WordSentence);

export const {
  fetch: fetchWordSentencesAssociation,
  fetchCanonical: fetchWordSentencesAssociationCanonical,
  search: searchWordSentencesAssociation,
  saveDeep: saveWordSentencesAssociation,
} = externalAssociationActions(
  API.wordSentencesAssociationRoutes,
  Schema.WordSentences,
  {
    canonicalArgs: {
      [SearchParameter.Draft]: Draft.Both,
    },
  },
);

export const createWordSentenceInAssociation = (wordId, wordSentenceId) =>
  saveEntityDeep(fetchWordSentencesAssociationCanonical, (data) =>
    sessionRequest(
      API.wordSentencesAssociationRoutes.put(wordId, {
        ...data,
      }),
    ),
    Schema.WordSentence,
  )(wordSentenceId);

export const {
  fetch: fetchSentence,
  fetchList: fetchSentences,
  search: searchSentences,
  remove: removeSentence,
  save: saveSentence,
} = entityActions(API.sentenceRoutes, Schema.Sentence);

export const createWord = createEntity(
  (data) => sessionRequest(API.postWords(data)),
  new Schema.Word({
    word:     '',
    pronunciations: List([]),
    language: ENGLISH.id,
  }));

// Usage Question / Answer Actions
export const {
  fetch: fetchUsageQuestion,
  fetchList: fetchUsageQuestions,
  remove: removeUsageQuestion,
  save: saveUsageQuestion,
} = entityActions(API.usageQuestionsRoutes, Schema.WordUsageQuestion);

/**
 * Action for posting bulk uknow question and answers as a CSV file
 * @param file The FormData corresponding to a CSV file in the "file" param containing uknow questions and answers
 * @returns data the corresponding question and answers created by the backend on a successful upload.
 *
 * The file form should be a single csv file stored in the `file` key of the form data.
 */
export const postQuestions = (file) => (dispatch) => dispatch(sessionRequest(API.uploadUsageQuestions(file)));

export const {
  fetch: fetchWordUsageQuestions,
  fetchCanonical: fetchWordUsageQuestionsCanonical,
  save: saveWordUsageQuestions,
} = externalAssociationActions(API.wordUsageQuestionAssociationRoutes, Schema.WordUsageQuestion);

// Vocab List Actions

export const {
  fetch: fetchVocabListHeader,
  fetchList: fetchVocabListHeaders,
  search: searchVocabListHeaders,
} = entityActions(API.vocabListHeaderRoutes, Schema.VocabListHeader);

export const {
  fetch: fetchVocabList,
  fetchList: fetchVocabLists,
  remove: removeVocabList,
  save: saveVocabList,
} = entityActions(API.vocabListRoutes, Schema.VocabList);

// User Actions

export const {
  fetch: fetchUser,
  search: searchUsers,
  fetchList: fetchUsers,
  remove: removeUser,
  save: saveUser,
} = entityActions(API.userRoutes, Schema.User);

export const {
  fetch: fetchUserProfile,
  search: searchUserProfiles,
  fetchList: fetchUserProfiles,
} = entityActions(API.userProfileRoutes, Schema.UserProfile);

export const {
  fetch: fetchUserDetail,
  search: searchUserDetails,
  fetchList: fetchUserDetails,
} = entityActions(API.userDetailRoutes, Schema.UserDetail);

export const likeAnswer = (id) => (dispatch) => {
  const postLike = sessionRequest(API.likeUsageAnswerRoute(id));
  return dispatch(postLike);
};

export const unlikeAnswer = (answerId) => dispatch =>
  dispatch(sessionRequest(API.unlikeUsageAnswerRoute(answerId)),
);

export const createUser = (id) => (dispatch) => {
  const postEntity = (edits) => sessionRequest(API.createUser(edits));
  return dispatch(saveEntityOnce(postEntity, Schema.User, id));
};

export const signup = (id, recaptchaToken, signupStateParam) => async (dispatch, getState) => {
  const { email: username, password } = getEntityOfType(getState(), Schema.User, id);
  const postEntity = (edits) => sessionRequest(API.signup(edits, recaptchaToken, signupStateParam));
  await dispatch(saveEntityOnce(postEntity, Schema.User, id));
  return await dispatch(authenticate(username, password));
};

export const {
  fetchList: fetchBlockedEmailDomains,
  search: searchBlockedEmailDomains,
  remove: removeBlockedEmailDomain,
  save: saveBlockedEmailDomain,
} = entityActions(API.blockedEmailDomainRoutes, Schema.BlockedEmailDomain);

// Run a set of actions against the application reducer, and serialize
// the changes to the given root. Useful for implementing imperative
// actions that are expressed as viewmodel updates.
const serializeEffects = (state, type, id, actions) => {
  const baseState    = rootReducer(state, discardLocalChanges());
  const updatedState = actions.reduce(rootReducer, baseState);
  return serializeEdits(updatedState, type, id);
};

export const detachUserExternalAuth = (userId, externalAuthId) => (dispatch, getState) => {
  const body = serializeEffects(getState(), Schema.User, userId, [
    removeListElement({
      type:      Schema.User,
      id:        userId,
      attribute: 'userExternalAuths',
      value:     externalAuthId,
    }),
  ]);

  return dispatch(postAndUpdate(sessionRequest(API.userRoutes.post(body))));
};


export const {
  fetch: fetchStaffMember,
  search: searchStaffMembers,
  fetchList: fetchStaffMembers,
  remove: removeStaffMember,
  save: saveStaffMember,
} = entityActions(API.staffMemberRoutes, Schema.StaffMember);

export const {
  fetch: fetchCommonText,
  search: searchCommonTexts,
  fetchList: fetchCommonTexts,
  remove: removeCommonText,
  save: saveCommonText,
} = entityActions(API.commonTextRoutes, Schema.CommonText);

export const createCommonText = createEntity(
  (data) => sessionRequest(API.postCommonTexts(data)),
  new Schema.CommonText({
    text:     '',
    language: ENGLISH.id,
  }));

export const validateLesson = (lessonID) => (dispatch) =>
  dispatch(sessionRequest(API.validateLesson(lessonID))).then((response) => {
    dispatch(clearValidationErrors(
      LESSON_VALIDATION_SOURCE, lessonID));
  }).catch((failure) => {
    if ((failure instanceof ApplicationError) && isValidationError(failure)) {
      dispatch(setValidationErrors(
        LESSON_VALIDATION_SOURCE, lessonID, failure.causes));
    } else {
      notify(failure);
      throw failure;
    }
  });

export const clearLessonValidation = (lessonID) =>
  clearValidationErrors(LESSON_VALIDATION_SOURCE, lessonID);

/**
 * @param {String} lessonId
 * @param {String} locationType
 * @param {String} locationId
 * @param {String} message
 */
export const createLessonIssue = (lessonId, locationType, locationId, message) => (dispatch) => {
  const { postLessonReportIssue } = API;

  const actionCreator = createEntity(
    (data) => sessionRequest(postLessonReportIssue(lessonId, data)),
    new Schema.IssueReport());

  const data = {
    locationType,
    locationId,
    message,
  };

  return dispatch(actionCreator(data));
};

export const {
  fetch: fetchDialogSpeaker,
  fetchList: fetchDialogSpeakers,
  search: searchDialogSpeakers,
  remove: removeDialogSpeaker,
  save: saveDialogSpeaker,
} = entityActions(API.dialogSpeakerRoutes, Schema.DialogSpeaker);

export const createDialogSpeaker = createEntity(
  data => sessionRequest(API.postDialogSpeakers(data)),
  new Schema.DialogSpeaker({
    name: '',
    language: ENGLISH.id,
  }),
);

const {
  fetch:     fetchMeetingBookingBase,
  fetchList: fetchMeetingBookings,
  remove:    removeMeetingBooking,
  save:      saveMeetingBooking,
} = entityActions(API.meetingBookingRoutes, Schema.MeetingBooking);

export const fetchMeetingBooking = (id, cancelToken) => async (dispatch, getState) => {
  const response = await dispatch(fetchMeetingBookingBase(id, cancelToken));

  const meetingBookingId = getResponseId(response);
  const brandId = getEntityOfTypeOrThrow(getState(), Schema.MeetingBooking, meetingBookingId).brand;
  const brand = brandId && getEntityOfType(getState(), Schema.Brand, brandId);

  if (!brand && brandId) {
    await dispatch(fetchBrand(brandId, cancelToken));
  }

  return response;
};

export {
  fetchMeetingBookings,
  removeMeetingBooking,
  saveMeetingBooking,
};

export const createMeetingBooking = createEntity(
  (data, cancelToken) => sessionRequest({ ...API.postMeetingBooking(data), cancelToken }),
  new Schema.MeetingBooking({ open: true }),
);

export const {
  fetch: fetchBotBooking,
} = entityActions(API.botBookingRoutes, Schema.MeetingBooking);

export const createBotBooking = (data) =>
  postAndUpdate(sessionRequest(API.postBotBooking(data)));

export const {
  fetch:     fetchLessonBooking,
  fetchList: fetchLessonBookings,
  save:      saveLessonBooking,
} = entityActions(API.lessonBookingRoutes, Schema.LessonBooking);

export const fetchCombinedLessonBooking = (id, cancelToken) => fetchEntity({
  params: API.getCombinedLessonBooking(id),
  type: Schema.CombinedLessonBooking,
  cancelToken,
});

export const createLessonBooking = (id) => async (dispatch, getState) => {
  const postEntity = (edits) => sessionRequest(API.postLessonBooking(edits));
  const response = await dispatch(saveEntityOnce(postEntity, Schema.LessonBooking, id));

  return getResponseId(response);
};

export const searchLessonSentences = (params) => async (dispatch) =>
  vmClient.recordize(await dispatch(sessionRequest(API.searchLessonSentences(params))));

export const {
  fetchList: fetchOauthApplications,
  remove: removeOauthApplication,
  save: saveOauthApplication,
} = entityActions(API.oauthApplicationRoutes, Schema.OauthApplication);

export const {
  fetch: fetchTeacher,
  fetchList: fetchTeachers,
  search: searchTeachers,
} = entityActions(API.teacherRoutes, Schema.Teacher);

export const fetchLatestTeacherApplicationForUser = (userId, cancelToken) => fetchEntity({
  params: API.getLatestTeacherApplicationForUser({ userId }),
  type: Schema.TeacherApplication,
  cancelToken,
});

export const {
  remove: deleteTeacherApplications,
  save: saveTeacherApplications,
} = entityActions(API.teacherApplicationRoutes, Schema.TeacherApplication);

export const saveUserTeacherApplication = (userId, applicationId) =>
  saveEntityOnce((data) =>
    sessionRequest(
      API.userTeacherApplicationRoutes.put(userId, {
        ...data,
      }),
    ),
    Schema.TeacherApplication,
    applicationId,
  );

export const {
  fetch: fetchBibliography,
  fetchList: fetchBibliographies,
  search: searchBibliographies,
  remove: removeBibliography,
  save: saveBibliography,
} = entityActions(API.bibliographyRoutes, Schema.Bibliography);

export const createBibliography = createEntity(
  data => sessionRequest(API.bibliographyRoutes.post(data)),
  new Schema.Bibliography({
    type: ARTICLE,
    title: 'Enter title',
    authors: null,
    publisher: 'Enter publisher',
    url: null,
    copyrightLicense: ALL_RIGHTS_RESERVED,
    licensePartner: null,
  }),
);
